import {createSelector} from '@reduxjs/toolkit';
import {conditional, conditionals} from './util/conditional';
import {getValueOfFeature, getValuesOfFeatureThatCanBeIncluded, hasFeatureValue, isSatisfied} from './util/values';
import {getProductData} from './util/getProductData';
import {meterToPx, mmToPx} from './util/meterToPx';
import {getArticleCode, getArticleCodeJoinedComponents, getLightengine1Code, getLightengine2Code} from './util/getArticleCode';
import {featureInSection} from './util/featureInSection';
import {getSectionConfig, luminaryOptions} from './sectionConfig';
import {lightProfileMap} from './lightProfiles';
import {solarDimmProfileOptions} from './solar/dimProfiles';

const selectTranslations = state => state.productData.translations;
const selectConditionalVariantValues = state => state.productData.conditionalVariantValues;
export const selectProductData = state => state.productData.productData;
export const selectFeatures = state => state.featureModel.data.features;
const selectVariantCodeTypeTemplate = state => state.productData.variantCodeTypeTemplate;
const selectStatusCurrentSection = state => state.status.currentSection;
const selectConfiguration = state => state.configuration.data;

export const selectDecisions = state => state.decisions.data;

export const selectSectionConfig = state => {
	const featureModelId = state.configuration && state.configuration.data.projects ? state.configuration.data.projects[0].project.featureModelId : null;
	const familyId = state.status ? state.status.family : null;
	return getSectionConfig(familyId, featureModelId);
};

export const selectLanguage = state => state.status.lang;

// Light Profile Configuration
export const selectBaseProfile = state => state.lightProfile.data.project?.base;
export const selectBaseProfileIsSatisfied = state => state.lightProfile.data.project?.base !== null;
export const selectZone = state => state.lightProfile.data.project?.zone;
export const selectZoneIsSatisfied = state => state.lightProfile.data.project?.zone !== null;
export const selectLightProfileLocation = state => state.lightProfile.data.project?.location;
export const selectLightProfileLocationIsSatisfied = state => state.lightProfile.data.project?.location !== null;
export const selectLuminary = state => state.lightProfile.data.project?.luminary;
export const selectLuminaryIsSatisfied = state => state.lightProfile.data.project?.luminary !== null;
export const selectProjectName = state => state.lightProfile.data.project.projectName;
export const selectPicoPassword = state => state.lightProfile.data.project.picoPassword;
export const selectCentralPowerControl = state => state.lightProfile.data.project?.centralPowerControl;
export const selectCentralPowerControlIsSatisfied = state => state.lightProfile.data.project?.centralPowerControl !== null;
export const selectDecisionsSatisfied = state => state.decisions.data.isSatisfied;

export const selectDimmLevel = state => state.configuration.data.projects[0].project.dimmLevelData;

export const selectProfileId = createSelector(
	[selectBaseProfile, selectZone, selectCentralPowerControl],
	(base, zone, centralPowerControl) => {
		const lightProfile = lightProfileMap[base];

		if (lightProfile && zone in lightProfile && centralPowerControl in lightProfile[zone]) {
			return lightProfile[zone][centralPowerControl];
		}

		return null;
	}
);


function conditionalSelectorFactory(id, type = 'EditorialTable', side = 1) {
	return createSelector([selectDecisions, selectConditionalVariantValues],
		(decisions, conditionalVariantValues) =>
			conditional(decisions, conditionalVariantValues, id, type, side)
	);
}

function hasFeatureValueSelectorFactory(featureId, valueId) {
	return createSelector([selectDecisions],
		(decisions) =>
			hasFeatureValue(featureId, valueId, decisions)
	);
}

export const selectCurrentSection = createSelector([selectSectionConfig, selectStatusCurrentSection],
	(sectionConfig, statusCurrentSection) => {
		const index = sectionConfig.findIndex(s => s.id === statusCurrentSection);
		if (statusCurrentSection && index !== -1) {
			return statusCurrentSection;
		} else {
			return sectionConfig[0].id;
		}
	}
);

export const selectArticleCode = createSelector([selectProductData, selectDecisions, selectFeatures],
	(productData, decisions, features) => (
		getArticleCode(decisions, features, productData)
	)
);
export const selectArticleCodeJoinedComponents = createSelector([selectProductData, selectDecisions, selectFeatures],
	(productData, decisions, features) => (
		getArticleCodeJoinedComponents(decisions, features, productData)
	)
);
export const selectLightengine1Code = createSelector([selectProductData, selectDecisions, selectFeatures],
	(productData, decisions, features) => (
		getLightengine1Code(decisions, features, productData)
	)
);
export const selectLightengine2Code = createSelector([selectProductData, selectDecisions, selectFeatures],
	(productData, decisions, features) => (
		getLightengine2Code(decisions, features, productData)
	)
);
export const selectProductModelId = createSelector([selectConfiguration],
	(configuration) => (
		configuration.projects[0].project.productModelId
	)
);

export const selectDiameter = conditionalSelectorFactory('HS_DIAMETERINMM', 'AdditionalAttribute');
export const selectIsOnesided = hasFeatureValueSelectorFactory('HS_ModuleCutoutSize_2', 'None');
export const selectIsBollard = hasFeatureValueSelectorFactory('HS_HousingType', 'Bollard');
export const selectIsColumn = hasFeatureValueSelectorFactory('HS_HousingType', 'Column');
export const selectHasPathway = hasFeatureValueSelectorFactory('HS_Construction', 'AsymmetricPathway');
export const selectHasCustomColor = hasFeatureValueSelectorFactory('HS_HousingColour', 'Custom');
export const selectLumen = conditionalSelectorFactory('REDTAB.RETURN_LE_LuminousFlux_Max');
export const selectPower = conditionalSelectorFactory('REDTAB.RETURN_EB_Wattage_Max');
export const selectWeight = conditionalSelectorFactory('REDTAB.RETURN_LU_Weight');
export const selectPolar = conditionalSelectorFactory('REDTAB.RETURN_PolarDiagramP2', 'EditorialTable', 1);
export const selectPolar2 = conditionalSelectorFactory('REDTAB.RETURN_PolarDiagramP2', 'EditorialTable', 2);
export const selectLePolar = conditionalSelectorFactory('REDTAB.RETURN_LE_PolarDiagramP2', 'EditorialTable', 1);
export const selectLePolar2 = conditionalSelectorFactory('REDTAB.RETURN_LE_PolarDiagramP2', 'EditorialTable', 2);
export const selectUlor = conditionalSelectorFactory('REDTAB.RETURN_LU_ULOR', 'EditorialTable', 1);
export const selectUlor2 = conditionalSelectorFactory('REDTAB.RETURN_LU_ULOR', 'EditorialTable', 2);
export const selectDatasetnumber = conditionalSelectorFactory('LE_LightEngineDataSetNumber', 'EditorialTable', 1);
export const selectDatasetnumber2 = conditionalSelectorFactory('LE_LightEngineDataSetNumber', 'EditorialTable', 2);
export const selectWindsurfacearea = conditionalSelectorFactory('HS_WindSurfaceArea');
export const selectModulesize = conditionalSelectorFactory('EB_ModuleType');
export const selectZhaga = conditionalSelectorFactory('EB_Zhaga');
export const selectHeight = conditionalSelectorFactory('HS_HousingHeight');
export const selectLLMFUrl = conditionalSelectorFactory('LE_LLMF_JSON');
export const selectCRI = conditionalSelectorFactory('LE_CRI');
export const selectDimensionHeight = conditionalSelectorFactory('REDTAB.RETURN_Dimension_Height');
export const selectDimensionWidth = conditionalSelectorFactory('REDTAB.RETURN_Dimension_Width');
export const selectDimensionDepth = conditionalSelectorFactory('REDTAB.RETURN_Dimension_Depth');
export const selectDimensionDiameter = conditionalSelectorFactory('Dimension_Diameter');

export const selectColorTemperature = createSelector([selectDecisions], (decisions) => (
		getValueOfFeature('LE_ColourTemperature_1', decisions)
	)
);

export const selectHeightDecided = createSelector([selectDecisions], (decisions) => (
		isSatisfied('HS_HousingHeight', decisions)
	)
);
export const selectModuleDecided = createSelector([selectDecisions], (decisions) => (
		isSatisfied('LE_LightEngineModuleSize_1', decisions)
	)
);
export const selectConstructionDecided = createSelector([selectDecisions], (decisions) => (
		isSatisfied('HS_Construction', decisions)
	)
);
export const selectHasBuriedBase = createSelector([selectDecisions], (decisions) => (
		hasFeatureValue('HS_Base', 'BuriedBase', decisions) ||
		hasFeatureValue('HS_Base', 'BuriedBaseShort', decisions)
	)
);
export const selectHasBase = createSelector([selectDecisions], (decisions) => (
		!hasFeatureValue('HS_Base', 'None', decisions)
	)
);
export const selectIsSingle = createSelector([selectDecisions], (decisions) => (
		!hasFeatureValue('HS_HousingType', 'Column', decisions) &&
		!hasFeatureValue('HS_HousingType', 'Bollard', decisions) &&
		!hasFeatureValue('HS_HousingType', 'ColumnTwist06', decisions) &&
		!hasFeatureValue('HS_HousingType', 'ColumnTwist12', decisions)
	)
);
export const selectSystemLumen = createSelector([selectDecisions, selectConditionalVariantValues],
	(decisions, conditionalVariantValues) => {
		const lumens = conditionals(decisions, conditionalVariantValues, 'REDTAB.RETURN_LE_LuminousFlux_Max');
		return lumens ? lumens.reduce((sum, lumen) => (parseInt(lumen.value, 10) + sum), 0) : undefined;
	}
);
export const selectSystemLumenString = createSelector([selectDecisions, selectConditionalVariantValues],
	(decisions, conditionalVariantValues) => {
		const lumens = conditionals(decisions, conditionalVariantValues, 'REDTAB.RETURN_LE_LuminousFlux_Max');
		return lumens ? lumens.map((lumen) => (parseInt(lumen.value, 10))).join(' + ') : '';
	}
);
export const selectSystemPower = createSelector([selectDecisions, selectConditionalVariantValues],
	(decisions, conditionalVariantValues) => {
		const lumens = conditionals(decisions, conditionalVariantValues, 'REDTAB.RETURN_EB_Wattage_Max');
		return lumens ? lumens.reduce((sum, lumen) => (parseInt(lumen.value, 10) + sum), 0) : undefined;
	}
);
export const selectSystemPowerString = createSelector([selectDecisions, selectConditionalVariantValues],
	(decisions, conditionalVariantValues) => {
		const lumens = conditionals(decisions, conditionalVariantValues, 'REDTAB.RETURN_EB_Wattage_Max');
		return lumens ? lumens.map((lumen) => (parseInt(lumen.value, 10))).join(' + ') : '';
	}
);
export const selectSystemLumenPerPower = createSelector([selectSystemLumen, selectSystemPower],
	(lumen, power) => {
		const lpp = Math.round(lumen / power);
		return !isNaN(lpp) ? lpp : undefined;
	}
);
export const selectModulePos = createSelector([selectDecisions, selectConditionalVariantValues],
	(decisions, conditionalVariantValues) => (
		{
			m1top: conditional(decisions, conditionalVariantValues, 'REDTAB.RETURN_Distance_Module1_From_Top'),
			m1height: conditional(decisions, conditionalVariantValues, 'REDTAB.RETURN_Total_Height_Module1'),
			m2height: conditional(decisions, conditionalVariantValues, 'REDTAB.RETURN_Total_Height_Module2'),
			between: conditional(decisions, conditionalVariantValues, 'REDTAB.RETURN_Distance_Between_Modules')
		}
	)
);
export const selectModulePosPx = createSelector([selectModulePos],
	(modulePos) => (
		{
			m1top: meterToPx(modulePos.m1top),
			m1height: meterToPx(modulePos.m1height),
			m2height: meterToPx(modulePos.m2height),
			between: meterToPx(modulePos.between)
		}
	)
);
export const selectTotalHeightPx = createSelector([selectDecisions, selectConditionalVariantValues],
	(decisions, conditionalVariantValues) =>
		meterToPx(conditional(decisions, conditionalVariantValues, 'REDTAB.RETURN_Total_Height_L')) / 1000
);
export const selectMeasures = createSelector([selectDecisions, selectConditionalVariantValues, selectIsBollard, selectHasPathway],
	(decisions, conditionalVariantValues, isBollard, hasPathway) => {
		// fixed heights
		const pathwayHeight = mmToPx('2010'); // 230;
		const columnHeight = 553;
		const baseHeight = 85;

		const l = conditional(decisions, conditionalVariantValues, 'REDTAB.RETURN_Total_Height_L');
		const l1 = conditional(decisions, conditionalVariantValues, 'REDTAB.RETURN_Distance_L1');
		const l2 = conditional(decisions, conditionalVariantValues, 'REDTAB.RETURN_Distance_L2');
		const l3 = conditional(decisions, conditionalVariantValues, 'REDTAB.RETURN_Distance_L3');
		const l4 = conditional(decisions, conditionalVariantValues, 'REDTAB.RETURN_Distance_L4');
		const l5 = conditional(decisions, conditionalVariantValues, 'REDTAB.RETURN_Distance_L5');
		const p1 = conditional(decisions, conditionalVariantValues, 'REDTAB.RETURN_Distance_P1');
		const p2 = conditional(decisions, conditionalVariantValues, 'REDTAB.RETURN_Distance_P2');
		const p3 = conditional(decisions, conditionalVariantValues, 'REDTAB.RETURN_Distance_P3');
		const p4 = conditional(decisions, conditionalVariantValues, 'REDTAB.RETURN_Distance_P4');
		const u = conditional(decisions, conditionalVariantValues, 'REDTAB.RETURN_Distance_U');

		// calculate pixel top and heights from top to bottom
		const lTop = 0;
		const lHeight = isBollard ? mmToPx(l) : columnHeight;

		const l1Top = 0;
		const l1Height = mmToPx(l1);

		const l2Top = l1Top + l1Height;
		const l2Height = mmToPx(l2);

		const l3Top = l2Top + l2Height;
		const l3Height = hasPathway ? (columnHeight - pathwayHeight) - l3Top : mmToPx(l3);

		const l4Top = hasPathway ? columnHeight - pathwayHeight : l3Top + l3Height;
		const l4Height = mmToPx(l4);

		// from bottom up
		const p1Height = mmToPx(p1);
		const p2Height = mmToPx(p2);
		const p3Height = mmToPx(p3);
		const p4Height = mmToPx(p4);

		const l5Top = l4Top + l4Height;
		// column has cut
		const l5Height = isBollard ? mmToPx(l5) : (columnHeight - (p1Height + p2Height)) - l5Top;

		const p4Top = isBollard ? l5Top + l5Height : columnHeight - (p1Height + p2Height + p3Height + p4Height);
		const p3Top = isBollard ? l5Top + l5Height : columnHeight - (p1Height + p2Height + p3Height);
		const p2Top = isBollard ? l5Top + l5Height : columnHeight - (p1Height + p2Height);
		const p1Top = isBollard ? p2Top + p2Height : columnHeight - p1Height;

		// base
		const uTop = lHeight;
		const uHeight = mmToPx(u) > 0 ? baseHeight : 0;

		return {
			l: {
				label: 'L',
				mm: l + ' mm',
				top: lTop,
				height: lHeight,
				hidden: false
			},
			l1: {
				label: 'L1',
				mm: l1 + ' mm',
				top: l1Top,
				height: l1Height,
				hidden: l1Height === 0
			},
			l2: {
				label: 'L2',
				mm: l2 + ' mm',
				top: l2Top,
				height: l2Height,
				hidden: l2Height === 0
			},
			l3: {
				label: 'L3',
				mm: l3 + ' mm',
				top: l3Top,
				height: l3Height,
				hidden: l3Height === 0
			},
			l4: {
				label: 'L4',
				mm: l4 + ' mm',
				top: l4Top,
				height: l4Height,
				hidden: l4Height === 0
			},
			l5: {
				label: 'L5',
				mm: l5 + ' mm',
				top: l5Top,
				height: l5Height,
				hidden: true
			},
			// from bottom
			p4: {
				label: 'P4',
				mm: p4 + ' mm',
				top: p4Top,
				height: p4Height,
				hidden: p4Height === 0
			},
			p3: {
				label: 'P3',
				mm: p3 + ' mm',
				top: p3Top,
				height: p3Height,
				hidden: p3Height === 0
			},
			p2: {
				label: 'P2',
				mm: p2 + ' mm',
				top: p2Top,
				height: p2Height,
				hidden: p2Height === 0
			},
			p1: {
				label: 'P1',
				mm: p1 + ' mm',
				top: p1Top,
				height: p1Height,
				hidden: p1Height === 0
			},
			u: {
				label: 'U',
				mm: u + ' mm',
				top: uTop,
				height: uHeight,
				hidden: uHeight === 0
			}
		};
	}
);
export const selectLdts = createSelector([selectDecisions, selectConditionalVariantValues],
	(decisions, conditionalVariantValues) => {
		// make sure we are getting ldts in the right order
		const ldt1 = conditional(decisions, conditionalVariantValues, 'REDTAB.RETURN_LE_LDTFile', 'EditorialTable', 1);
		const ldt2 = conditional(decisions, conditionalVariantValues, 'REDTAB.RETURN_LE_LDTFile', 'EditorialTable', 2);
		return [
			ldt1 ? {
				url: ldt1,
				name: ldt1.split('/').pop()
			} : null,
			ldt2 ? {
				url: ldt2,
				name: ldt2.split('/').pop()
			} : null
		];
	});
export const selectLdt = createSelector([selectDecisions, selectConditionalVariantValues],
	(decisions, conditionalVariantValues) => (
		conditional(decisions, conditionalVariantValues, 'REDTAB.RETURN_LE_LDTFile', 'EditorialTable', 1)
	));
export const selectLdt1 = createSelector([selectDecisions, selectConditionalVariantValues],
	(decisions, conditionalVariantValues) => (
		conditional(decisions, conditionalVariantValues, 'REDTAB.RETURN_LE_LDTFile', 'EditorialTable', 1)
	));
export const selectLdt2 = createSelector([selectDecisions, selectConditionalVariantValues],
	(decisions, conditionalVariantValues) => (
		conditional(decisions, conditionalVariantValues, 'REDTAB.RETURN_LE_LDTFile', 'EditorialTable', 2)
	));
export const selectMountingInstruction = createSelector([selectDecisions, selectConditionalVariantValues],
	(decisions, conditionalVariantValues) => (
		conditional(decisions, conditionalVariantValues, 'REDTAB.RETURN_MountingInstructionLink', 'EditorialTable', 1)
	));
export const selectFeatureModelCompletionPercentage = createSelector([selectDecisions, selectFeatures, selectProductData], (decisions, features, productData) => {
	if (features) {
		const visibleCount = features
			.filter(feature => getProductData(feature.variableId, productData)?.isVisible)
			.length ?? 100;
		const satisfiedCount = features
			.filter(feature => getProductData(feature.variableId, productData)?.isVisible && isSatisfied(feature.variableId, decisions))
			.length ?? 0;
		return Math.round((satisfiedCount / visibleCount * 100));
	} else {
		return 0;
	}
});
export const selectLightProfilePercentage = createSelector([selectBaseProfileIsSatisfied, selectZoneIsSatisfied, selectLightProfileLocationIsSatisfied, selectLuminaryIsSatisfied, selectFeatureModelCompletionPercentage],
	(base, zone, location, luminary, luminaryPercentage) => (
		(luminary ?
				// if luminary is satisfied, add 40% of luminaryPercentage to the total
				((base ? 20 : 0) + (zone ? 20 : 0) + (location ? 20 : 0) + (luminary ? luminaryPercentage * 0.4 : 0)) :
				// if luminary is not satisfied, ignore luminaryPercentage
				((base ? 33 : 0) + (zone ? 33 : 0) + (location ? 34 : 0))
		)
	)
);
export const selectLightProfileIsSatisfied = createSelector([selectBaseProfileIsSatisfied, selectZoneIsSatisfied, selectLightProfileLocationIsSatisfied, selectLuminaryIsSatisfied, selectFeatureModelCompletionPercentage],
	(base, zone, location, luminary, percentage) => (
		(luminary ?
				(base && zone && location && luminary && percentage >= 100) :
				(base && zone && location)
		)
	)
);
export const selectUnsatisfied = createSelector([selectSectionConfig, selectFeatures, selectProductData, selectDecisions], (sectionsConfig, features, productData, decisions) => {
	if (!sectionsConfig || !features || !productData || !decisions) return null;
	const unsatisfiedSections = sectionsConfig
		.filter(section => !features
			.filter(feature => featureInSection(sectionsConfig, section, feature.variableId))
			.filter(feature => getProductData(feature.variableId, productData)?.isVisible)
			.reduce((acc, feature) => acc && isSatisfied(feature.variableId, decisions), true)
		)
		.map(section => ({
			sectionId: section.id,
			unsatisfiedFeatures: features
				.filter(feature => featureInSection(sectionsConfig, section, feature.variableId))
				.filter(feature => getProductData(feature.variableId, productData)?.isVisible)
				.filter(feature => !isSatisfied(feature.variableId, decisions))
				.map(feature => feature.variableId)
		}));
	return unsatisfiedSections;
});
export const selectVariantCodeTypes = createSelector([selectDecisions, selectVariantCodeTypeTemplate, selectConditionalVariantValues],
	(decisions, variantCodeTypeTemplates, conditionalVariantValues) => {
		const variantCodes = [];
		variantCodeTypeTemplates.forEach(variantCodeTypeTemplate => {
			const name = variantCodeTypeTemplate.templateName;
			const definition = variantCodeTypeTemplate.templateDefinition;
			const codeMappings = variantCodeTypeTemplate.codeMappings;
			let result = '' + definition;
			const vars = definition.match(/{([^{}]+)}/g).map(match => match.replace(/[{}]/g, ''));
			vars.forEach(variable => {
				let featureId = variable;
				let replacement = '';
				if (variable.indexOf(':') !== -1) {
					featureId = variable.split(':')[0];
					replacement = variable.split(':')[1];
				}

				// the value of the feature
				const value = getValueOfFeature(featureId, decisions);
				// the conditional value with the variable name
				const conditionalValue = conditional(decisions, conditionalVariantValues, variable);
				let code = '';
				if (conditionalValue) {
					// use the conditional, when available
					code = conditionalValue;
				} else {
					// otherwise use the code mapping for the value
					const mapping = codeMappings.find(codeMap => codeMap.attributeId === featureId && codeMap.attributeValueId === value);
					if (mapping) {
						code = replacement === '' ? mapping.code : replacement;
					}
				}

				result = result.replace(`{${featureId}}`, code);
			});
			result = result.replace(/{([^{}]+)}/g, '');
			variantCodes.push({
				id: name,
				code: result
			});
		});
		return variantCodes;
	});

export const selectMajorNumbers = (state) => state.status.numbers.majorNumbers;
export const selectIntermediateNumbers = (state) => state.status.numbers.intermediateNumbers;

export const selectSystemLuminousFluxMax = createSelector([selectDecisions, selectConditionalVariantValues],
	(decisions, conditionalVariantValues) => (
		conditional(decisions, conditionalVariantValues, 'LU_SystemLuminousFluxMax', 'EditorialTable', 1)
	));
export const selectSystemLuminousFluxMin = createSelector([selectDecisions, selectConditionalVariantValues],
	(decisions, conditionalVariantValues) => (
		conditional(decisions, conditionalVariantValues, 'LU_SystemLuminousFluxMin', 'EditorialTable', 1)
	));
export const selectAvailableDimmingLevel = createSelector([selectDecisions, selectConditionalVariantValues],
	(decisions, conditionalVariantValues) => (
		getValuesOfFeatureThatCanBeIncluded('LE_DimmingLevel_1', decisions, conditionalVariantValues)
	));
export const selectStepsForLevel = createSelector([selectDecisions, selectConditionalVariantValues],
	(decisions, conditionalVariantValues) => (
		conditional(decisions, conditionalVariantValues, 'LU_SystemLuminousFluxMin', 'EditorialTable', 1)
	));
export const selectCoefficients = createSelector([selectDecisions, selectConditionalVariantValues],
	(decisions, conditionalVariantValues) => ({
			formular1: {
				type: conditional(decisions, conditionalVariantValues, 'Formula1_Type', 'EditorialTable', 1),
				coefficients: [
					parseFloat(conditional(decisions, conditionalVariantValues, 'Formula1_Parameter00', 'EditorialTable', 1)?.replace(',', '.')),
					parseFloat(conditional(decisions, conditionalVariantValues, 'Formula1_Parameter01', 'EditorialTable', 1)?.replace(',', '.')),
					parseFloat(conditional(decisions, conditionalVariantValues, 'Formula1_Parameter02', 'EditorialTable', 1)?.replace(',', '.'))
				]
			},
			formular2: {
				type: conditional(decisions, conditionalVariantValues, 'Formula2_Type', 'EditorialTable', 1),
				coefficients: [
					parseFloat(conditional(decisions, conditionalVariantValues, 'Formula2_Parameter00', 'EditorialTable', 1)?.replace(',', '.')),
					parseFloat(conditional(decisions, conditionalVariantValues, 'Formula2_Parameter01', 'EditorialTable', 1)?.replace(',', '.')),
					parseFloat(conditional(decisions, conditionalVariantValues, 'Formula2_Parameter02', 'EditorialTable', 1)?.replace(',', '.'))
				]
			}
		}
	));

